import { createContext, useContext, useReducer, useCallback, useMemo, type ReactNode } from 'react';
import {
  createArtifactGraph,
  upsertArtifact,
  mergeArtifacts,
  selectByKind,
  getByName,
  latestBySource,
  metrics,
  type Artifact,
  type ArtifactGraph,
  type ArtifactKind,
  type StoreOperationResult
} from '@triedge/shared/artifacts';

/**
 * Context value shape
 */
export interface ArtifactGraphContextValue {
  graph: ArtifactGraph;
  upsert: (artifact: Omit<Artifact, 'id' | 'hash' | 'createdAt'>) => Artifact;
  merge: (artifacts: Array<Omit<Artifact, 'id' | 'hash' | 'createdAt'>>) => StoreOperationResult;
  byKind: (kind: ArtifactKind) => Artifact[];
  byName: (name: string) => Artifact | undefined;
  bySource: (source: string) => Artifact[];
  getMetrics: () => ReturnType<typeof metrics>;
  clear: () => void;
}

/**
 * Provider props
 */
export interface ArtifactGraphProviderProps {
  children: ReactNode;
  initialGraph?: ArtifactGraph;
}

/**
 * Reducer actions
 */
type Action =
  | { type: 'UPSERT'; payload: Omit<Artifact, 'id' | 'hash' | 'createdAt'> }
  | { type: 'MERGE'; payload: Array<Omit<Artifact, 'id' | 'hash' | 'createdAt'>> }
  | { type: 'CLEAR' }
  | { type: 'SET_GRAPH'; payload: ArtifactGraph };

/**
 * Reducer for artifact graph state
 */
function artifactGraphReducer(state: ArtifactGraph, action: Action): ArtifactGraph {
  switch (action.type) {
    case 'UPSERT': {
      const { graph } = upsertArtifact(state, action.payload);
      return graph;
    }
    case 'MERGE': {
      const { graph } = mergeArtifacts(state, action.payload);
      return graph;
    }
    case 'CLEAR':
      return createArtifactGraph();
    case 'SET_GRAPH':
      return action.payload;
    default:
      return state;
  }
}

/**
 * Create context with undefined default (will throw if used outside provider)
 */
const ArtifactGraphContext = createContext<ArtifactGraphContextValue | undefined>(undefined);

/**
 * Provider component that wraps @triedge/shared artifact store with React state management
 *
 * @example
 * ```tsx
 * <ArtifactGraphProvider>
 *   <YourApp />
 * </ArtifactGraphProvider>
 * ```
 */
export function ArtifactGraphProvider({ children, initialGraph }: ArtifactGraphProviderProps) {
  const [graph, dispatch] = useReducer(
    artifactGraphReducer,
    initialGraph ?? createArtifactGraph()
  );

  // Memoized action creators
  const upsert = useCallback(
    (artifact: Omit<Artifact, 'id' | 'hash' | 'createdAt'>) => {
      dispatch({ type: 'UPSERT', payload: artifact });
      // Return the artifact synchronously by upserting directly
      const { artifact: result } = upsertArtifact(graph, artifact);
      return result;
    },
    [graph]
  );

  const merge = useCallback(
    (artifacts: Array<Omit<Artifact, 'id' | 'hash' | 'createdAt'>>) => {
      dispatch({ type: 'MERGE', payload: artifacts });
      // Return result synchronously
      const { result } = mergeArtifacts(graph, artifacts);
      return result;
    },
    [graph]
  );

  const clear = useCallback(() => {
    dispatch({ type: 'CLEAR' });
  }, []);

  // Memoized selectors
  const byKind = useCallback(
    (kind: ArtifactKind) => selectByKind(graph, kind),
    [graph]
  );

  const byName = useCallback(
    (name: string) => getByName(graph, name),
    [graph]
  );

  const bySource = useCallback(
    (source: string) => latestBySource(graph, source),
    [graph]
  );

  const getMetrics = useCallback(() => metrics(graph), [graph]);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo<ArtifactGraphContextValue>(
    () => ({
      graph,
      upsert,
      merge,
      byKind,
      byName,
      bySource,
      getMetrics,
      clear
    }),
    [graph, upsert, merge, byKind, byName, bySource, getMetrics, clear]
  );

  return (
    <ArtifactGraphContext.Provider value={value}>
      {children}
    </ArtifactGraphContext.Provider>
  );
}

/**
 * Hook to access artifact graph context
 *
 * @throws {Error} If used outside ArtifactGraphProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { graph, byKind, getMetrics } = useArtifactGraphContext();
 *   const tables = byKind('table');
 *   return <div>Found {tables.length} tables</div>;
 * }
 * ```
 */
export function useArtifactGraphContext(): ArtifactGraphContextValue {
  const context = useContext(ArtifactGraphContext);
  if (!context) {
    throw new Error(
      'useArtifactGraphContext must be used within an ArtifactGraphProvider'
    );
  }
  return context;
}
